import { ArrowRight } from '@carbon/react/icons';
import styles from './process-flow.module.scss';

interface StepItem {
  kind: 'step';
  label: string;
}

interface ConnectorItem {
  kind: 'connector';
  label: string;
  sublabel?: string;
}

export type ProcessFlowItem = StepItem | ConnectorItem;

/**
 * Creates a step item for the process flow.
 * @param {string} label - The step label
 * @return {StepItem} A step item
 */
export const step = (label: string): StepItem => ({
  kind: 'step',
  label,
});

/**
 * Creates a connector item for the process flow.
 * @param {string} label - The connector label (e.g., "+10 months")
 * @param {string} sublabel - Optional sublabel
 * @return {ConnectorItem} A connector item
 */
export const connector = (label: string, sublabel?: string): ConnectorItem => ({
  kind: 'connector',
  label,
  sublabel,
});

interface ProcessFlowProps {
  items: ProcessFlowItem[];
}

/**
 * Renders a horizontal pipeline of steps connected by labeled arrows.
 * Steps render as styled pill spans, connectors render as label + arrow.
 * @param {ProcessFlowProps} props - The component props
 * @return {JSX.Element} The process flow visualization
 */
export function ProcessFlow({ items }: ProcessFlowProps): JSX.Element {
  return (
    <div className={styles.processFlow}>
      {items.map((item, i) => {
        if (item.kind === 'connector') {
          return (
            <div key={i} className={styles.connector}>
              <div className={styles.connectorText}>
                <span className={styles.connectorLabel}>{item.label}</span>
                {item.sublabel && (
                  <span className={styles.connectorSublabel}>
                    {item.sublabel}
                  </span>
                )}
              </div>
              <ArrowRight className={styles.arrow} />
            </div>
          );
        }
        return (
          <span key={i} className={styles.tag}>
            {item.label}
          </span>
        );
      })}
    </div>
  );
}
